import React from 'react';
import { observer } from 'mobx-react';
import { CSSObject } from 'styled-components';

import { StatusInfo } from 'apps/components/StatusInfo';
import { Heading, Box } from 'components';

import { useRouterStore } from 'apps/hooks';
import { formatDate } from 'utils/formatStrings';
import { PaymentStatusEnum } from 'apps/order/orderList/connManager';
import { ChangePortalBanner } from 'apps/client/components/ChangePortalBanner';
import { clientStore } from 'apps/client/app';
import { StatusSwitcher } from 'apps/components/StatusSwitcher';
import orderStatuses from 'apps/order/orderStatuses';
import type { OrderStatus } from 'apps/order/orderStatuses';

import { SpaceLink } from 'apps/shared';
import { ActionButtons } from './ActionButtons';

const textStyle: CSSObject = {
  fontWeight: 'normal',
};

interface OrderHeaderProps {
  orderAppManager: any;
}

export const OrderHeader = observer(function OrderHeader({
  orderAppManager,
}: OrderHeaderProps) {
  const { order } = orderAppManager;
  const routerStore = useRouterStore();
  const { params } = routerStore.route;
  const isIntermediator = clientStore.hasRole('INTERMEDIATOR');
  const paymentStatus =
    PaymentStatusEnum[order.paymentStatus as keyof typeof PaymentStatusEnum];
  const orderStatusObj = orderStatuses[order.orderStatus as OrderStatus];

  const isIntermediatorMode = clientStore.currentPortal === 'INTERMEDIATOR';

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'flex-start',
        justifyContent: 'space-between',
      }}
    >
      <Box>
        <Heading level={2} size='large' margin='0 16px 0 0'>
          Order{' '}
          <span style={textStyle}>
            #<span data-test-id='order-id'>{params.orderId}</span>
          </span>
        </Heading>
        {isIntermediator && order.quote && (
          <SpaceLink
            variant='primary'
            label='Order for'
            size='medium'
            space={order.quote.customerSpace}
          />
        )}
        <ChangePortalBanner />
        <Box mt={4}>
          {isIntermediatorMode ? (
            <StatusSwitcher
              value={order.orderStatus}
              nextPossibleStates={order.nextPossibleStatuses}
              onChange={orderAppManager.UpdateOrderStatus}
              type='order'
            />
          ) : (
            <Heading level={5} data-test-id='orderHeader-order-status'>
              <StatusInfo status={orderStatusObj} />
            </Heading>
          )}
          <Heading level={5} data-test-id='orderHeader-payment-status'>
            <StatusInfo label='Payment Status' status={paymentStatus} />
          </Heading>
          <Heading level={5}>
            Created Date:{' '}
            <span style={textStyle}>{formatDate(order.createdAt)}</span>
          </Heading>
          <Heading level={5}>
            Estimated Delivery:{' '}
            <span style={textStyle}>{formatDate(order.deliveryDate)}</span>
          </Heading>
        </Box>
      </Box>
      <ActionButtons receiptUrl={order.payments[0].receiptUrl} />
    </Box>
  );
});
